import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export interface User {
  id: string;
  name: string;
  username: string;
  email: string;
  cpf: string;
  profileId?: string;
  profileName?: string;
  active: boolean;
  lock: boolean;
  external: boolean;
  temporary_password: boolean;
  created_at: string;
  image?: {
    key: string;
  };
}

export interface CreateUserDTO {
  name: string;
  username: string;
  email: string;
  cpf: string;
  profileId: string;
  active?: boolean;
  external?: boolean;
  sendToEmail?: string;
}

export interface UpdateUserDTO {
  name?: string;
  username?: string;
  email?: string;
  cpf?: string;
  profileId?: string;
  active?: boolean;
  external?: boolean;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface UserQueryParams {
  page?: number;
  limit?: number;
  active?: boolean;
  name?: string;
  cpf?: string;
  username?: string;
  email?: string;
  profileId?: string;
  external?: boolean;
}

export const userApi = {
  findMany: async (params?: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const { data } = await api.get<ApiPaginatedResponse<User>>('/admin/users', { params });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<User> => {
    const { data } = await api.get<ApiResponse<User>>(`/admin/users/${id}`);
    return data.data;
  },

  create: async (dto: CreateUserDTO): Promise<User> => {
    const { data } = await api.post<ApiResponse<User>>('/admin/users', dto);
    return data.data;
  },

  update: async (id: string, dto: UpdateUserDTO): Promise<User> => {
    const { data } = await api.patch<ApiResponse<User>>(`/admin/users/${id}`, dto);
    return data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/users/${id}`);
  },

  lock: async (id: string, lock: boolean): Promise<{ success: boolean; lock: boolean }> => {
    const { data } = await api.post<ApiResponse<{ success: boolean; lock: boolean }>>(
      `/admin/users/${id}/lock`,
      { lock },
    );
    return data.data;
  },

  setActive: async (
    id: string,
    active: boolean,
  ): Promise<{ success: boolean; active: boolean }> => {
    const { data } = await api.post<ApiResponse<{ success: boolean; active: boolean }>>(
      `/admin/users/${id}/active`,
      { active },
    );
    return data.data;
  },

  resetPassword: async (
    id: string,
    sendToEmail?: string,
  ): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<ApiResponse<{ success: boolean; message: string }>>(
      `/admin/users/${id}/reset-password`,
      { sendToEmail },
    );
    return data.data;
  },

  // Get presigned download URL for avatar (15-min expiry)
  getAvatarUrl: async (userId: string, imageKey: string): Promise<string> => {
    const { data } = await api.get<ApiResponse<{ url: string }>>('/upload/avatar/url', {
      params: { userId, key: imageKey },
    });
    return data.data.url;
  },

  // Prepare avatar upload - get presigned S3 URL
  prepareAvatarUpload: async (params: {
    userId: string;
    fileName: string;
    contentType: string;
  }): Promise<{ uploadUrl: string; key: string; fileUrl: string }> => {
    const { data } = await api.post<
      ApiResponse<{ uploadUrl: string; key: string; fileUrl: string }>
    >('/upload/avatar/prepare', params);
    return data.data;
  },

  // Confirm avatar upload - save metadata to MongoDB
  confirmAvatarUpload: async (params: { userId: string; key: string }): Promise<void> => {
    await api.post('/upload/avatar/confirm', params);
  },

  findByProfile: async (
    profileId: string,
    params?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<User>> => {
    const { data } = await api.get<ApiPaginatedResponse<User>>('/admin/users', {
      params: { ...params, profileId },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },
};
